import { computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AGGREGATIONS_SIGNALR_HUB, LIVE_SIGNALR_HUB } from '@dxp/core/signalr';
import { environment } from '@dxp/core/environment';
import {
    Agent,
    IAgent,
    IAgentReport,
    IAgentSegment,
    IAgentState,
    IQueueActivity,
} from '@dxp/core/models';
import {
    signalStore,
    withComputed,
    withFeature,
    withHooks,
    withMethods,
    withState,
} from '@ngrx/signals';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    filter,
    interval,
    map,
    of,
    switchMap,
    tap,
} from 'rxjs';
import { updateState } from '@angular-architects/ngrx-toolkit';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { RbacApiService } from '@dxp/core/api';
import { ReloadService } from '@dxp/core/services';
import { withQueues } from '../queue/queue.feature';
import { QUEUE_STORE } from '../queue/queue.token';
import {
    areAgentReportsEqual,
    areAgentStatesEqual,
    areStringArraysEqualUnordered,
    asDate,
    deriveAgentIdsFromTeams,
} from './agent.helpers';

interface IAgentStoreState {
    agentIds: string[];
    report: Record<string, IAgentReport>;
    state: Record<string, IAgentState>;
}

const initialState: IAgentStoreState = {
    agentIds: [],
    report: {},
    state: {},
};

export const AgentStore = signalStore(
    { providedIn: 'root' },
    environment.storeWithDevTools('AgentStore'),
    withFeature(() => withQueues(inject(QUEUE_STORE))),
    withState<IAgentStoreState>(initialState),
    withMethods(() => {
        const tick = signal(0);

        return {
            incrementTick() {
                tick.update(v => v + 1);
            },

            get tick() {
                return tick;
            },
        };
    }),
    withComputed(store => ({
        activity: computed<Record<string, IQueueActivity[]>>(() => {
            const record: Record<string, IQueueActivity[]> = {};

            const activitiesWithAgentSegment = store
                .queues()
                .flatMap(q => q.activity())
                .filter(a => a.includeInReport && a.agentSegment);

            for (const activity of activitiesWithAgentSegment) {
                const segment = activity.agentSegment as IAgentSegment;

                for (const agent of segment.agents) {
                    const agentId = agent.id;

                    const list = record[agentId];
                    if (list) {
                        list.push(activity);
                    } else {
                        record[agentId] = [activity];
                    }
                }
            }

            return record;
        }),
    })),
    withComputed(store => ({
        ready: computed<boolean>(() => {
            const reports = store.report();
            const states = Object.values(store.state())
                .filter(as => store.agentIds().includes(as.agentId))
                .filter(as => as.agentName !== '')
                .map(as => as.agentId);

            return states.every(id => id in reports);
        }),
        agents: computed<IAgent[]>(() => {
            store.tick();

            const ids = Object.values(store.state())
                .filter(as => store.agentIds().includes(as.agentId))
                .filter(as => as.agentName !== '')
                .map(as => as.agentId);
            const reports = store.report();
            const states = store.state();
            const activities = store.activity();

            return ids.map(id => {
                const report = reports[id];
                const state = states[id];
                const activity = activities[id];

                return new Agent(id, report, state, activity);
            });
        }),
    })),
    withMethods(store => ({
        updateAgentsFromTeams(agentIds: string[]) {
            updateState(store, 'UPDATE_AGENT_ARRAY', state => {
                const prevAgentIds = state.agentIds;

                if (areStringArraysEqualUnordered(prevAgentIds, agentIds)) {
                    return state;
                }

                return {
                    ...state,
                    agentIds: agentIds,
                };
            });
        },
        updateAgentReport(report: IAgentReport) {
            const normalized: IAgentReport = {
                ...report,
                loggedInAt: asDate(report.loggedInAt),
                loggedOutAt: asDate(report.loggedOutAt),
            };

            updateState(store, 'UPDATE_AGENT_REPORT', state => {
                const prev = state.report[normalized.id];

                if (!prev) {
                    return {
                        ...state,
                        report: {
                            ...state.report,
                            [normalized.id]: normalized,
                        },
                    };
                }

                if (areAgentReportsEqual(prev, normalized)) {
                    return state;
                }

                return {
                    ...state,
                    report: {
                        ...state.report,
                        [normalized.id]: normalized,
                    },
                };
            });
        },
        updateAgentStates(agentStates: IAgentState[]) {
            agentStates
                .filter(a => a.agentName !== '')
                .forEach(agentState => {
                    const normalized: IAgentState = {
                        ...agentState,
                        stateSince: asDate(agentState.stateSince) as Date,
                        breakExpiresAt: asDate(agentState.breakExpiresAt),
                    };

                    updateState(store, 'UPDATE_AGENT_STATE', state => {
                        const prev = state.state[normalized.agentId];

                        if (!prev) {
                            return {
                                ...state,
                                state: {
                                    ...state.state,
                                    [normalized.agentId]: normalized,
                                },
                            };
                        }

                        if (areAgentStatesEqual(prev, normalized)) {
                            return state;
                        }

                        return {
                            ...state,
                            state: {
                                ...state.state,
                                [normalized.agentId]: normalized,
                            },
                        };
                    });
                });
        },
    })),
    withHooks({
        onInit(
            store,
            aggregationsHubService = inject(AGGREGATIONS_SIGNALR_HUB),
            liveHubService = inject(LIVE_SIGNALR_HUB),
            oidcSecurityService = inject(OidcSecurityService),
            rbacApiService = inject(RbacApiService),
            reloadService = inject(ReloadService),
            destroyRef = inject(DestroyRef),
        ) {
            combineLatest(
                oidcSecurityService.userData$,
                toObservable(reloadService.refreshTime),
                toObservable(reloadService.refreshTeams),
            )
                .pipe(
                    map(([user, refresh, refreshTeams]) => ({
                        userId: user?.userData?.sub as unknown,
                        refresh,
                        refreshTeams,
                    })),
                    filter(
                        (
                            x,
                        ): x is {
                            userId: string;
                            refresh: number;
                            refreshTeams: number;
                        } =>
                            typeof x.userId === 'string' && x.userId.length > 0,
                    ),
                    distinctUntilChanged(
                        (a, b) =>
                            a.userId === b.userId &&
                            a.refresh === b.refresh &&
                            a.refreshTeams === b.refreshTeams,
                    ),
                    switchMap(({ userId }) =>
                        rbacApiService.getTeams(userId).pipe(
                            catchError(error => {
                                reloadService.error(
                                    'AGENT_STORE',
                                    error.message,
                                );

                                return of([]);
                            }),
                        ),
                    ),
                    map(teams => deriveAgentIdsFromTeams(teams)),
                    distinctUntilChanged(areStringArraysEqualUnordered),
                    tap(agentIds => store.updateAgentsFromTeams(agentIds)),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            aggregationsHubService.agentReport$
                .pipe(
                    tap(report => store.updateAgentReport(report)),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            liveHubService.agents$
                .pipe(
                    tap(states => store.updateAgentStates(states)),
                    takeUntilDestroyed(destroyRef),
                )
                .subscribe();

            interval(1000)
                .pipe(takeUntilDestroyed(destroyRef))
                .subscribe(() => store.incrementTick());
        },
    }),
);
